import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookieConsent';
const COOKIE_PREFERENCES_KEY = 'cookiePreferences';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent flash
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const saveConsent = (acceptAll = false) => {
    const newPreferences = acceptAll 
      ? { essential: true, analytics: true, marketing: true }
      : preferences;
    
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowSettings(false);
    
    // Dispatch event for other parts of the app to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
      detail: newPreferences 
    }));
  };

  const rejectAll = () => {
    const minimalPreferences = { essential: true, analytics: false, marketing: false };
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(minimalPreferences));
    setPreferences(minimalPreferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="cookie-consent-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="cookie-consent-banner"
      >
        <motion.div
          className="cookie-consent-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
        >
          {!showSettings ? (
            // Main Banner
            <div className="cookie-main">
              <div className="cookie-header">
                <Cookie size={24} />
                <h3>Cookie Preferences</h3>
              </div>
              
              <p className="cookie-text">
                We use cookies to enhance your experience on BlackGirlTrading-Academy. Essential cookies are 
                necessary for the site to function. You can choose to accept or reject optional 
                cookies below. For more information, please read our{' '}
                <Link to="/privacy-policy" onClick={() => setShowBanner(false)}>Privacy Policy</Link>.
              </p>
              
              <div className="cookie-actions">
                <button 
                  className="btn btn-outline"
                  onClick={rejectAll}
                  data-testid="cookie-reject-btn"
                >
                  Reject All
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowSettings(true)}
                  data-testid="cookie-settings-btn"
                >
                  <Settings size={16} />
                  Manage Preferences
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => saveConsent(true)}
                  data-testid="cookie-accept-btn"
                >
                  <Check size={16} />
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="cookie-settings">
              <div className="cookie-header">
                <button 
                  className="back-btn"
                  onClick={() => setShowSettings(false)}
                >
                  ←
                </button>
                <h3>Cookie Settings</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowBanner(false)}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="cookie-options">
                {/* Essential Cookies */}
                <div className="cookie-option">
                  <div className="cookie-option-header">
                    <div className="cookie-option-info">
                      <h4>Essential Cookies</h4>
                      <span className="cookie-badge required">Required</span>
                    </div>
                    <label className="toggle disabled">
                      <input type="checkbox" checked disabled />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>
                    These cookies are necessary for the website to function properly. 
                    They enable core functionality like authentication, security, and accessibility.
                  </p>
                </div>
                
                {/* Analytics Cookies */}
                <div className="cookie-option">
                  <div className="cookie-option-header">
                    <div className="cookie-option-info">
                      <h4>Analytics Cookies</h4>
                      <span className="cookie-badge optional">Optional</span>
                    </div>
                    <label className="toggle">
                      <input 
                        type="checkbox" 
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          analytics: e.target.checked
                        })}
                        data-testid="analytics-toggle"
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>
                    These cookies help us understand how visitors interact with our website. 
                    This information is used to improve our services and user experience.
                  </p>
                </div>
                
                {/* Marketing Cookies */}
                <div className="cookie-option">
                  <div className="cookie-option-header">
                    <div className="cookie-option-info">
                      <h4>Marketing Cookies</h4>
                      <span className="cookie-badge optional">Optional</span>
                    </div>
                    <label className="toggle">
                      <input 
                        type="checkbox" 
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({
                          ...preferences,
                          marketing: e.target.checked
                        })}
                        data-testid="marketing-toggle"
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>
                    These cookies are used to deliver relevant advertisements and track 
                    campaign performance. They may be set by our advertising partners.
                  </p>
                </div>
              </div>
              
              <div className="cookie-actions">
                <button 
                  className="btn btn-outline"
                  onClick={rejectAll}
                >
                  Reject Optional
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => saveConsent(false)}
                  data-testid="cookie-save-btn"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>

      <style>{`
        .cookie-consent-overlay {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 9999;
          padding: 16px;
          pointer-events: none;
        }

        .cookie-consent-banner {
          max-width: 680px;
          margin: 0 auto;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.4);
          pointer-events: all;
        }

        .cookie-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .cookie-header svg {
          color: var(--primary);
        }

        .cookie-header h3 {
          font-size: 1.125rem;
          margin: 0;
          flex: 1;
        }

        .cookie-text {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 20px;
        }

        .cookie-text a {
          color: var(--primary);
          text-decoration: underline;
        }

        .cookie-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .cookie-actions .btn {
          flex: 1;
          min-width: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--primary);
          border: none;
          color: white;
        }

        .btn-primary:hover {
          background: var(--primary-light);
        }

        .btn-secondary {
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          color: var(--text-primary);
        }

        .btn-secondary:hover {
          background: var(--bg-paper);
          border-color: var(--primary);
        }

        .btn-outline {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }

        .btn-outline:hover {
          border-color: var(--text-muted);
          color: var(--text-primary);
        }

        /* Settings Panel */
        .cookie-settings .cookie-header {
          position: relative;
        }

        .back-btn {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 20px;
          cursor: pointer;
          padding: 4px 8px;
        }

        .back-btn:hover {
          color: var(--text-primary);
        }

        .close-btn {
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 4px;
          margin-left: auto;
        }

        .close-btn:hover {
          color: var(--text-primary);
        }

        .cookie-options {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
        }

        .cookie-option {
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
        }

        .cookie-option-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .cookie-option-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .cookie-option h4 {
          margin: 0;
          font-size: 14px;
        }

        .cookie-option p {
          margin: 0;
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.5;
        }

        .cookie-badge {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }

        .cookie-badge.required {
          background: rgba(0, 255, 148, 0.15);
          color: var(--success);
        }

        .cookie-badge.optional {
          background: rgba(100, 100, 255, 0.15);
          color: #8888ff;
        }

        /* Toggle Switch */
        .toggle {
          position: relative;
          display: inline-block;
          width: 48px;
          height: 26px;
        }

        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: var(--bg-paper);
          border: 1px solid var(--border);
          transition: 0.3s;
          border-radius: 26px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 2px;
          bottom: 2px;
          background-color: var(--text-muted);
          transition: 0.3s;
          border-radius: 50%;
        }

        .toggle input:checked + .slider {
          background-color: var(--primary);
          border-color: var(--primary);
        }

        .toggle input:checked + .slider:before {
          transform: translateX(22px);
          background-color: white;
        }

        .toggle.disabled {
          opacity: 0.7;
        }

        .toggle.disabled .slider {
          cursor: not-allowed;
          background-color: var(--primary);
          border-color: var(--primary);
        }

        .toggle.disabled .slider:before {
          transform: translateX(22px);
          background-color: white;
        }

        @media (max-width: 600px) {
          .cookie-consent-banner {
            padding: 20px;
          }

          .cookie-actions {
            flex-direction: column;
          }

          .cookie-actions .btn {
            width: 100%;
          }
        }
      `}</style>
    </AnimatePresence>
  );
};

// Helper function to check consent status
export const getCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!consent) return null;
  
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  return prefs ? JSON.parse(prefs) : { essential: true, analytics: false, marketing: false };
};

export default CookieConsent;
